import { useEffect, useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api'

interface ManagerRow { id: string; name: string; phone: string; isActive: boolean }
interface PointRow { id: string; name: string }

interface Props {
  open: boolean
  onClose: () => void
  defaultManagerId?: string
  defaultPointId?: string
}

function apiError(err: unknown): string {
  const e = err as { response?: { data?: { error?: { message?: string } } } }
  return e?.response?.data?.error?.message ?? 'Не удалось создать смену'
}

/** Format a Date into the `value` shape accepted by <input type="datetime-local">. */
function toLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export default function ManualShiftModal({ open, onClose, defaultManagerId, defaultPointId }: Props) {
  const qc = useQueryClient()
  const [managerId, setManagerId] = useState(defaultManagerId ?? '')
  const [pointId, setPointId] = useState(defaultPointId ?? '')
  const [openedAtLocal, setOpenedAtLocal] = useState('')
  const [closedAtLocal, setClosedAtLocal] = useState('')
  const [cashAtOpen, setCashAtOpen] = useState('0')
  const [notes, setNotes] = useState('')
  const [submitError, setSubmitError] = useState<string | null>(null)

  const { data: managersData } = useQuery<{ managers: ManagerRow[] }>({
    queryKey: ['managers'],
    queryFn: () => api.get('/users').then((r) => r.data),
    enabled: open,
  })
  const { data: pointsData } = useQuery<{ points: PointRow[] }>({
    queryKey: ['points'],
    queryFn: () => api.get('/points').then((r) => r.data),
    enabled: open,
  })

  const managers = (managersData?.managers ?? []).filter((m) => m.isActive)
  const points = pointsData?.points ?? []

  // Reset on open
  useEffect(() => {
    if (open) {
      setManagerId(defaultManagerId ?? '')
      setPointId(defaultPointId ?? '')
      setOpenedAtLocal('')
      setClosedAtLocal('')
      setCashAtOpen('0')
      setNotes('')
      setSubmitError(null)
    }
  }, [open, defaultManagerId, defaultPointId])

  // Cascade preview — recalc when point + openedAt are set
  const openedAtIso = useMemo(() => {
    if (!openedAtLocal) return null
    const d = new Date(openedAtLocal)
    if (isNaN(d.getTime())) return null
    return d.toISOString()
  }, [openedAtLocal])

  const { data: cascade } = useQuery<{ count: number; limit: number }>({
    queryKey: ['cascade-preview', pointId, openedAtIso],
    queryFn: () => api.get('/shifts/cascade-preview', { params: { pointId, from: openedAtIso } }).then((r) => r.data),
    enabled: open && Boolean(pointId && openedAtIso),
  })

  const create = useMutation({
    mutationFn: (payload: {
      managerId: string; pointId: string;
      openedAt: string; closedAt: string | null;
      cashAtOpen: number; notes: string;
    }) => api.post('/shifts/manual', payload).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['shifts'] })
      qc.invalidateQueries({ queryKey: ['payments-list'] })
      onClose()
    },
    onError: (e) => setSubmitError(apiError(e)),
  })

  const openedAtDate = openedAtLocal ? new Date(openedAtLocal) : null
  const closedAtDate = closedAtLocal ? new Date(closedAtLocal) : null
  const cashNum = parseFloat(cashAtOpen.replace(',', '.'))

  const validations: string[] = []
  if (!managerId) validations.push('Выберите менеджера')
  if (!pointId) validations.push('Выберите точку')
  if (!openedAtDate || isNaN(openedAtDate.getTime())) validations.push('Введите дату открытия')
  if (openedAtDate && openedAtDate > new Date()) validations.push('Дата открытия в будущем')
  if (closedAtDate && openedAtDate && closedAtDate <= openedAtDate) validations.push('Закрытие должно быть позже открытия')
  if (isNaN(cashNum) || cashNum < 0) validations.push('Касса на открытие — число ≥ 0')
  if (notes.trim().length < 3) validations.push('Комментарий минимум 3 символа')

  const handleSubmit = () => {
    setSubmitError(null)
    if (validations.length > 0) return
    create.mutate({
      managerId,
      pointId,
      openedAt: openedAtDate!.toISOString(),
      closedAt: closedAtDate ? closedAtDate.toISOString() : null,
      cashAtOpen: cashNum,
      notes: notes.trim(),
    })
  }

  if (!open) return null

  // Pre-fill defaults for datetime inputs: today 09:00 / today 21:00
  const setQuickToday = () => {
    const t = new Date()
    t.setHours(9, 0, 0, 0)
    setOpenedAtLocal(toLocalInput(t))
    t.setHours(21, 0, 0, 0)
    setClosedAtLocal(toLocalInput(t))
  }

  const cascadeOver = cascade && cascade.count > cascade.limit

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md p-4 space-y-3 max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="font-bold">📝 Ручная смена</h2>
          <button onClick={onClose} className="text-gray-400 text-2xl leading-none">×</button>
        </div>

        <p className="text-xs text-gray-500 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5">
          Для смен из тетради. Никаких автоматических уведомлений менеджеру не уйдёт.
        </p>

        <label className="block text-xs text-gray-600">
          Менеджер <span className="text-red-500">*</span>
          <select value={managerId} onChange={(e) => setManagerId(e.target.value)}
            className="w-full mt-1 border rounded-lg px-2 py-2 text-sm">
            <option value="">—</option>
            {managers.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        </label>

        <label className="block text-xs text-gray-600">
          Точка <span className="text-red-500">*</span>
          <select value={pointId} onChange={(e) => setPointId(e.target.value)}
            className="w-full mt-1 border rounded-lg px-2 py-2 text-sm">
            <option value="">—</option>
            {points.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </label>

        <div className="grid grid-cols-2 gap-2">
          <label className="block text-xs text-gray-600">
            Открытие <span className="text-red-500">*</span>
            <input type="datetime-local" value={openedAtLocal}
              onChange={(e) => setOpenedAtLocal(e.target.value)}
              className="w-full mt-1 border rounded-lg px-2 py-2 text-sm" />
          </label>
          <label className="block text-xs text-gray-600">
            Закрытие <span className="text-gray-400">(если уже закрыта)</span>
            <input type="datetime-local" value={closedAtLocal}
              onChange={(e) => setClosedAtLocal(e.target.value)}
              className="w-full mt-1 border rounded-lg px-2 py-2 text-sm" />
          </label>
        </div>

        <button type="button" onClick={setQuickToday}
          className="text-xs text-brand-600 hover:underline">
          Быстро: сегодня 09:00 → 21:00
        </button>

        <label className="block text-xs text-gray-600">
          Касса на открытие, ₽
          <input type="number" min="0" step="0.01" value={cashAtOpen}
            onChange={(e) => setCashAtOpen(e.target.value)}
            className="w-full mt-1 border rounded-lg px-2 py-2 text-sm" />
        </label>

        <label className="block text-xs text-gray-600">
          Комментарий (источник данных) <span className="text-red-500">*</span>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
            placeholder="Например: тетрадь 06.05, точка Центр"
            rows={2}
            className="w-full mt-1 border rounded-lg px-2 py-2 text-sm" />
        </label>

        {cascade && cascade.count > 0 && (
          <div className={`rounded-lg px-3 py-2 text-xs ${cascadeOver ? 'bg-red-50 border border-red-200 text-red-700' : 'bg-blue-50 border border-blue-200 text-blue-700'}`}>
            {cascadeOver
              ? `⚠️ Каскадный пересчёт затронет ${cascade.count} смен — больше лимита (${cascade.limit}). Создать не получится.`
              : `🔄 Будет пересчитано ${cascade.count} смен на этой точке (касса на открытие в цепочке).`
            }
          </div>
        )}

        {validations.length > 0 && (
          <ul className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 space-y-0.5">
            {validations.map((v) => <li key={v}>• {v}</li>)}
          </ul>
        )}
        {submitError && (
          <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{submitError}</div>
        )}

        <div className="flex gap-2 pt-1">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-lg border border-gray-300 text-sm">
            Отмена
          </button>
          <button
            onClick={handleSubmit}
            disabled={create.isPending || validations.length > 0 || cascadeOver === true}
            className="flex-1 py-2.5 rounded-lg bg-brand-600 text-white text-sm font-semibold disabled:opacity-50"
          >
            {create.isPending ? 'Создание…' : 'Создать'}
          </button>
        </div>
      </div>
    </div>
  )
}
